import React, { useState } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Sparkles, Users, KeyRound, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import QedaraLogo from "@/components/QedaraLogo";

export default function WorkspaceOnboarding() {
  const { createWorkspace, joinWorkspace } = useWorkspace();
  const { user, logout } = useAuth();
  const [mode, setMode] = useState("create");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "create") {
        if (!name.trim()) return;
        await createWorkspace(name.trim());
        toast.success(`Workspace-ul „${name}" e gata`);
      } else {
        await joinWorkspace(code.trim());
        toast.success("Ai intrat în workspace");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu a reușit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-fg grid-bg p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <QedaraLogo variant="full" markSize={32} />
          <button data-testid="onboarding-logout" onClick={logout} className="text-fg/40 hover:text-fg text-xs flex items-center gap-1.5">
            <LogOut className="w-3.5 h-3.5" /> {user?.name}
          </button>
        </div>

        <div className="bg-surface border border-line/10">
          <div className="p-6 border-b border-line/10">
            <p className="label-tiny mb-2">// PRIMA PORNIRE</p>
            <h1 className="text-3xl font-bold tracking-tighter mb-2">Pornește-ți echipa.</h1>
            <p className="text-fg/60 text-sm">Creează un workspace nou sau intră în unul cu un cod de invitație.</p>
          </div>

          <div className="flex border-b border-line/10">
            <button
              data-testid="onboarding-tab-create"
              onClick={() => setMode("create")}
              className={`flex-1 py-3 text-sm flex items-center justify-center gap-2 transition-colors ${
                mode === "create" ? "bg-fg/5 text-fg border-b-2 border-brand -mb-[2px]" : "text-fg/50 hover:text-fg"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" /> Creează
            </button>
            <button
              data-testid="onboarding-tab-join"
              onClick={() => setMode("join")}
              className={`flex-1 py-3 text-sm flex items-center justify-center gap-2 transition-colors ${
                mode === "join" ? "bg-fg/5 text-fg border-b-2 border-brand -mb-[2px]" : "text-fg/50 hover:text-fg"
              }`}
            >
              <KeyRound className="w-3.5 h-3.5" /> Intră
            </button>
          </div>

          <form onSubmit={submit} className="p-6 space-y-4">
            {mode === "create" ? (
              <>
                <label className="label-tiny block">Numele workspace-ului</label>
                <input
                  data-testid="onboarding-workspace-name-input"
                  className="input-flat"
                  autoFocus value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Acme Engineering"
                />
                <p className="text-[11px] text-fg/40 mono">
                  Se creează 3 canale implicite (#general, #vibe-coding, #random).
                </p>
              </>
            ) : (
              <>
                <label className="label-tiny block">Cod de invitație</label>
                <input
                  data-testid="onboarding-invite-code-input"
                  className="input-flat mono uppercase"
                  autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="VIBE-XXXXXX"
                />
                <p className="text-[11px] text-fg/40 mono">Cere codul administratorului workspace-ului.</p>
              </>
            )}
            <button
              data-testid="onboarding-submit-button"
              type="submit" disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {busy ? "..." : mode === "create" ? "Creează workspace" : "Intră într-un workspace"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        <p className="text-center text-[11px] text-fg/30 mono mt-6">
          Ești autentificat ca {user?.email}
        </p>
      </div>
    </div>
  );
}
